import { AwaitQueue } from 'awaitqueue';
import { inject, injectable } from 'inversify';
import { serializeError } from 'serialize-error';
import {
  CLIENT_EVENTS,
  INTERNAL_CLIENT_EVENTS,
  MEDIASOUP_EVENTS,
  PEER_EVENTS,
} from '../../constants/events';
import { TemporalLayer } from '../../constants/videoConstrains';
import Media from '../media';
import EnhancedEventEmitter from '../../EnhancedEventEmitter';
import { TOKEN } from '../../inversify.tokens';
import {
  Logger,
  TrackForceClosedPayload,
} from '../../types/common';
import PeersStorage from '../PeersStorage';
import { MediaEvent, MediaEventType } from '../../proto/generated/channel_message/media_event';
import {
  convertChannelMessageProducerLabelsToTrackLabels,
  convertChannelMessageProducerToProducerData,
} from '../../helpers/channelMessageConverter';

@injectable()
class MediaEventHandler {
  readonly #queue = new AwaitQueue();

  readonly #logger: Logger;

  constructor(
    @inject(TOKEN.Media) private readonly media: Media,
    @inject(TOKEN.InternalEventEmitter) private readonly internalEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.PeersStorage) private readonly peersStorage: PeersStorage,
    @inject(TOKEN.ClientEventEmitter) private readonly clientEventEmitter: EnhancedEventEmitter,
    @inject(TOKEN.LoggerFactory) loggerFactory: (namespace: string) => Logger,
  ) {
    this.#logger = loggerFactory('MediaEvents');
  }

  public handleMediaEvent(event: MediaEvent): void {
    switch (event.eventType) {
      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_CREATED:
        this.handleProducerCreated(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_CLOSED:
        this.handleProducerClosed(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_PAUSED:
        this.handleProducerPaused(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_RESUMED:
        this.handleProducerResumed(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_MAX_SPATIAL_LAYER_CHANGED:
        this.handleProducerMaxSpatialLayerChanged(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_MAX_SPATIAL_LAYER_REQUESTED:
        this.handleProducerRequestMaxSpatialLayer(event).catch((error: unknown) => {
          this.#logger.error('Failed to handle producer max spatial layer requested event', {
            error: serializeError(error),
          });
        });
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_PRODUCER_FORCIBLY_CLOSED:
        this.handleProducerForciblyClosed(event).catch((error: unknown) => {
          this.#logger.error('Failed to handle producer forcibly closed event', { error: serializeError(error) });
        });
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_CONSUMER_CLOSED:
        // do nothing, we handle consumer close on producer close event,
        // since producer close causes all consumers to close
        this.#logger.debug('Close consumer event received, skip', { consumerId: event.consumer?.id });
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_CONSUMER_RESUMED:
        this.handleConsumerResumed(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_CONSUMER_PAUSED:
        this.handleConsumerPaused(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_CONSUMER_PREFERRED_LAYERS_CHANGED:
        this.handleConsumerPreferredLayersChanged(event);
        break;

      case MediaEventType.MEDIA_EVENT_TYPE_TRANSPORT_CONNECTION_TIMEOUT:
        this.handleTransportConnectionTimeout(event);
        break;

      default:
        throw new Error(`Unsupported event type: ${ event.eventType }`);
    }
  }

  private handleProducerCreated(event: MediaEvent): void {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_CREATED');
    }

    const peer = this.peersStorage.get(event.producer.peerId);
    peer?.observer.safeEmit(MEDIASOUP_EVENTS.newProducer, convertChannelMessageProducerToProducerData(event.producer));
  }

  private handleProducerClosed(event: MediaEvent): void {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_CLOSED');
    }

    const peer = this.peersStorage.get(event.producer.peerId);
    peer?.observer.safeEmit(
      MEDIASOUP_EVENTS.producerClose,
      convertChannelMessageProducerToProducerData(event.producer),
    );
  }

  private handleProducerPaused(event: MediaEvent): void {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_PAUSED');
    }

    const peer = this.peersStorage.get(event.producer.peerId);
    const producer = peer?.producers.get(event.producer.id);

    if (!peer) {
      this.#logger.debug('Peer not found for producer paused event', { peerId: event.producer.peerId });

      return;
    }

    if (!producer) {
      peer.observer.safeEmit(MEDIASOUP_EVENTS.newProducer, {
        ...convertChannelMessageProducerToProducerData(event.producer),
        paused: true,
      });

      return;
    }

    producer.setPaused(true);
    peer.observer.safeEmit(PEER_EVENTS.publisherPaused, {
      producerId: producer.id,
      kind: producer.kind,
      label: producer.label,
      paused: producer.paused,
    });
  }

  private handleProducerResumed(event: MediaEvent): void {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_RESUMED');
    }

    const peer = this.peersStorage.get(event.producer.peerId);
    const producer = peer?.producers.get(event.producer.id);

    if (!peer) {
      this.#logger.debug('Peer not found for producer resumed event', { peerId: event.producer.peerId });

      return;
    }

    if (!producer) {
      peer.observer.safeEmit(MEDIASOUP_EVENTS.newProducer, {
        ...convertChannelMessageProducerToProducerData(event.producer),
        paused: false,
      });

      return;
    }

    producer.setPaused(false);
    peer.observer.safeEmit(PEER_EVENTS.publisherResumed, {
      producerId: producer.id,
      kind: producer.kind,
      label: producer.label,
      paused: producer.paused,
    });
  }

  private handleProducerMaxSpatialLayerChanged(event: MediaEvent): void {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_MAX_SPATIAL_LAYER_CHANGED');
    }

    if (event.producer.maxSpatialLayer === undefined) {
      return;
    }

    const peer = this.peersStorage.get(event.producer.peerId);
    peer?.observer.safeEmit(MEDIASOUP_EVENTS.producerSetMaxSpatialLayer, {
      peerId: event.producer.peerId,
      producerId: event.producer.id,
      spatialLayer: event.producer.maxSpatialLayer,
    });
  }

  private async handleProducerRequestMaxSpatialLayer(event: MediaEvent): Promise<void> {
    if (!event.producerSpatialLayerRequest) {
      throw new Error('Missing producerSpatialLayerRequest in MEDIA_EVENT_TYPE_PRODUCER_MAX_SPATIAL_LAYER_REQUESTED');
    }

    const { producerId, spatialLayer } = event.producerSpatialLayerRequest;

    await this.#queue.push(async () => {
      await this.media.handleProducerSetMaxSpatialLayer({ producerId, spatialLayer });
    }, MEDIASOUP_EVENTS.producerRequestMaxSpatialLayer);
  }

  private async handleProducerForciblyClosed(event: MediaEvent): Promise<void> {
    if (!event.producer) {
      throw new Error('Missing producer in MEDIA_EVENT_TYPE_PRODUCER_FORCIBLY_CLOSED');
    }

    const label = convertChannelMessageProducerLabelsToTrackLabels(event.producer.label);
    const track = this.media.getTrackByLabel(label);

    if (!track) {
      return;
    }

    track.stopMediaStreamTrack();
    this.internalEventEmitter.safeEmit(INTERNAL_CLIENT_EVENTS.trackClosed, track);
    const payload: TrackForceClosedPayload = { label };
    this.clientEventEmitter.emit(CLIENT_EVENTS.trackForceClosed, payload);
    await this.media.deleteTrack(track);
  }

  private handleConsumerResumed(event: MediaEvent): void {
    if (!event.consumer) {
      throw new Error('Missing consumer in MEDIA_EVENT_TYPE_CONSUMER_RESUMED');
    }

    const peer = this.peersStorage.get(event.consumer.producerPeerId);
    peer?.observer.safeEmit(MEDIASOUP_EVENTS.resumeConsumer, event.consumer.id);
  }

  private handleConsumerPaused(event: MediaEvent): void {
    if (!event.consumer) {
      throw new Error('Missing consumer in MEDIA_EVENT_TYPE_CONSUMER_PAUSED');
    }

    const peer = this.peersStorage.get(event.consumer.producerPeerId);
    peer?.observer.safeEmit(MEDIASOUP_EVENTS.pauseConsumer, event.consumer.id);
  }

  private handleConsumerPreferredLayersChanged(event: MediaEvent): void {
    if (!event.consumer) {
      throw new Error('Missing consumer in MEDIA_EVENT_TYPE_CONSUMER_PREFERRED_LAYERS_CHANGED');
    }

    if (event.consumer.spatialLayer === undefined) {
      return;
    }

    const producerPeerId = event.consumer.producerPeerId || event.consumer.peerId;
    const peer = this.peersStorage.get(producerPeerId);

    peer?.observer.safeEmit(MEDIASOUP_EVENTS.consumerChangePreferredLayers, {
      consumerId: event.consumer.id,
      spatialLayer: event.consumer.spatialLayer,
      temporalLayer: event.consumer.temporalLayer ?? TemporalLayer.BASE,
    });
  }

  private handleTransportConnectionTimeout(event: MediaEvent): void {
    if (!event.transportConnectionTimeout) {
      throw new Error('Missing transportConnectionTimeout in MEDIA_EVENT_TYPE_TRANSPORT_CONNECTION_TIMEOUT');
    }

    this.#logger.info('Transport connection timed out', {
      transportId: event.transportConnectionTimeout.transportId,
      direction: event.transportConnectionTimeout.direction,
      timeout: event.transportConnectionTimeout.timeout,
      reason: event.transportConnectionTimeout.reason,
    });
  }
}

export default MediaEventHandler;
